import { environment } from 'src/environments/environment';

export interface AppConfig {
  API_CMS_URL: string;
  INFO_API_URL: string;
  KEYCLOAK_REALM: string;
  KEYCLOAK_URL: string;
  KEYCLOAKCLIENT_ID: string;
  KEYCLOAK_SESSION_TIMEOUT: number;
}

type RawAppConfig = Partial<Record<keyof AppConfig, unknown>>;

const RUNTIME_APP_CONFIG_URL = '/assets/environment.json';
const UNRESOLVED_ENV_VAR_PATTERN = /^\$[A-Z0-9_]+$/;
const EMPTY_APP_CONFIG: AppConfig = {
  API_CMS_URL: '',
  INFO_API_URL: '',
  KEYCLOAK_REALM: '',
  KEYCLOAK_URL: '',
  KEYCLOAKCLIENT_ID: '',
  KEYCLOAK_SESSION_TIMEOUT: 0,
};

function isUnresolvedEnvVar(value: string): boolean {
  return UNRESOLVED_ENV_VAR_PATTERN.test(value.trim());
}

function normalizeStringValue(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmedValue = value.trim();
  return isUnresolvedEnvVar(trimmedValue) ? '' : trimmedValue;
}

function normalizeTimeoutValue(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (trimmedValue === '' || isUnresolvedEnvVar(trimmedValue)) {
      return 0;
    }

    const parsed = Number(trimmedValue);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  return 0;
}

export function hasConfiguredString(value: unknown): boolean {
  return normalizeStringValue(value) !== '';
}

function quoteBareEnvPlaceholders(jsonText: string): string {
  return jsonText.replace(/:\s*(\$[A-Z0-9_]+)(\s*[,}])/g, (_match, placeholder, suffix) => `: "${placeholder}"${suffix}`);
}

export function normalizeAppConfig(config: RawAppConfig | null | undefined): AppConfig {
  return {
    API_CMS_URL: normalizeStringValue(config?.API_CMS_URL),
    INFO_API_URL: normalizeStringValue(config?.INFO_API_URL),
    KEYCLOAK_REALM: normalizeStringValue(config?.KEYCLOAK_REALM),
    KEYCLOAK_URL: normalizeStringValue(config?.KEYCLOAK_URL),
    KEYCLOAKCLIENT_ID: normalizeStringValue(config?.KEYCLOAKCLIENT_ID),
    KEYCLOAK_SESSION_TIMEOUT: normalizeTimeoutValue(config?.KEYCLOAK_SESSION_TIMEOUT),
  };
}

export function parseAppConfigText(text: string): AppConfig {
  try {
    return normalizeAppConfig(JSON.parse(quoteBareEnvPlaceholders(text)) as RawAppConfig);
  } catch (error) {
    console.warn('Could not parse runtime app config. Falling back to build-time defaults.', error);
    return { ...EMPTY_APP_CONFIG };
  }
}

export async function loadAppConfigFromUrl(url = RUNTIME_APP_CONFIG_URL): Promise<AppConfig> {
  const response = await fetch(url);
  const text = await response.text();
  return parseAppConfigText(text);
}

/** Apply each configured runtime field independently; empty/placeholder values keep build-time defaults. */
export function applyEnvironmentOverrides(config: AppConfig): void {
  if (hasConfiguredString(config.API_CMS_URL)) {
    environment.endpointBasePaths.cmsApiUrl = config.API_CMS_URL;
  }
  if (hasConfiguredString(config.INFO_API_URL)) {
    environment.endpointBasePaths.infoApiUrl = config.INFO_API_URL;
  }
  if (hasConfiguredString(config.KEYCLOAK_REALM)) {
    environment.keycloak.realm = config.KEYCLOAK_REALM;
  }
  if (hasConfiguredString(config.KEYCLOAK_URL)) {
    environment.keycloak.url = config.KEYCLOAK_URL;
  }
  if (hasConfiguredString(config.KEYCLOAKCLIENT_ID)) {
    environment.keycloak.clientId = config.KEYCLOAKCLIENT_ID;
  }

  if (config.KEYCLOAK_SESSION_TIMEOUT > 0) {
    environment.keycloak.sessionTimeout = config.KEYCLOAK_SESSION_TIMEOUT;
  }
}
